/**
 * Custom-field `_fields` path helpers.
 * Projection rules: `docs/layers/api-client.md`.
 */

/**
 * Sub-paths that turn a customer-kind custom field into a five-key contact stub.
 */
export const CUSTOM_FIELD_CUSTOMER_VALUE_SUBPATHS = ['_id', 'name', 'status', 'user.picture', 'cover_url'] as const;

export const CUSTOM_FIELD_LOCATION_KIND = 'location';

export const CUSTOM_FIELD_LOCATION_VALUE_SUBPATHS = [
	'_id',
	'name',
	'icon',
	'locationPath._id',
	'locationPath.name',
	'locationPath.icon',
] as const;

export const expandLocationCustomFieldPaths = (root: string): string[] =>
	CUSTOM_FIELD_LOCATION_VALUE_SUBPATHS.map((subpath) => `${root}.${subpath}`);

const CUSTOM_FIELD_REFERENCE_VALUE_SUBPATHS = [
	...CUSTOM_FIELD_CUSTOMER_VALUE_SUBPATHS,
	...CUSTOM_FIELD_LOCATION_VALUE_SUBPATHS,
].sort((left, right) => right.length - left.length);

/**
 * Wildcard stubs for the whole `fields` bag when no column→kind map is available.
 * Prefer named stubs via `expandCustomFieldPaths` when `fieldKinds` is known.
 */
export const CUSTOM_FIELD_WILDCARD_STUB_PATHS = CUSTOM_FIELD_CUSTOMER_VALUE_SUBPATHS.map(
	(sub) => `fields.*.${sub}` as const
);

export const CUSTOM_FIELD_CUSTOMER_KIND = 'customer';

/** True when the projection asks for the custom-fields bag (needs expand stubs for customer refs). */
export const projectionIncludesCustomFieldsBag = (fields: readonly string[]): boolean =>
	fields.some((field) => field === 'fields' || field === 'fields.*' || field.startsWith('fields.*.'));

/**
 * Own (`fields.<name>`) or one-segment nested (`customer.fields.<name>`, `reporter.fields.<name>`).
 * Deeper prefixes (e.g. `order.customer.fields.X`) are not treated as custom-field columns today.
 */
const isCustomFieldColumnPath = (field: string): boolean =>
	field.startsWith('fields.') || /^[^.]+\.fields\./.test(field);

const ownCustomFieldPath = (field: string): string | undefined => {
	if (field.startsWith('fields.')) return field;
	const nestedIndex = field.indexOf('.fields.');
	if (nestedIndex === -1) return undefined;
	return field.slice(nestedIndex + 1);
};

const kindForCustomFieldRoot = (root: string, fieldKinds?: Readonly<Record<string, string>>): string | undefined => {
	if (!fieldKinds) return undefined;
	const ownPath = ownCustomFieldPath(root);
	return fieldKinds[root] ?? (ownPath ? fieldKinds[ownPath] : undefined);
};

/**
 * If `field` is already a known column root (or a named stub of one), return the root.
 * Prefer a full-path kind hit before treating a trailing segment as a stub suffix — custom
 * field names may end with `.name`, `.status`, etc.
 */
const customFieldColumnRoot = (field: string, fieldKinds?: Readonly<Record<string, string>>): string => {
	if (kindForCustomFieldRoot(field, fieldKinds)) return field;
	for (const sub of CUSTOM_FIELD_REFERENCE_VALUE_SUBPATHS) {
		const suffix = `.${sub}`;
		if (!field.endsWith(suffix)) continue;
		const root = field.slice(0, -suffix.length);
		if (isCustomFieldColumnPath(root)) return root;
	}
	return field;
};

export const expandCustomerCustomFieldPaths = (fieldPath: string): string[] =>
	CUSTOM_FIELD_CUSTOMER_VALUE_SUBPATHS.map((sub) => `${fieldPath}.${sub}`);

/**
 * Build a column-id → kind map from overview column configs.
 * Only custom-field ids (`fields.<name>`, `customer.fields.<name>`, …) are kept.
 */
export const customFieldKindsFromColumns = (
	columns: readonly { id: string; kind?: string | null }[]
): Record<string, string> => {
	const kinds: Record<string, string> = {};
	for (const column of columns) {
		if (!column.kind || !isCustomFieldColumnPath(column.id)) continue;
		kinds[column.id] = column.kind;
	}
	return kinds;
};

/**
 * Rewrite custom field columns in a caller `fields` list. Reference-kind columns become
 * their named display stub paths; scalars stay as `fields.<name>`. Prefix-matched because
 * custom field names may contain dots. See `docs/layers/api-client.md`.
 */
export const expandCustomFieldPaths = (
	fields: readonly string[],
	{ fieldKinds }: { fieldKinds?: Readonly<Record<string, string>> } = {}
): string[] =>
	fields.flatMap((field) => {
		if (!isCustomFieldColumnPath(field)) return [field];
		const root = customFieldColumnRoot(field, fieldKinds);
		const kind = kindForCustomFieldRoot(root, fieldKinds);
		if (kind === CUSTOM_FIELD_CUSTOMER_KIND) {
			return expandCustomerCustomFieldPaths(root);
		}
		if (kind === CUSTOM_FIELD_LOCATION_KIND) {
			return expandLocationCustomFieldPaths(root);
		}
		return [field];
	});
